import { CountryCode, parsePhoneNumber } from 'libphonenumber-js'
import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { CompleteUser, Profile, SocialData } from '../@types'
import { useAppStatus } from '../hooks/use-app-status'
import { PROFILE_VALIDATION_ERROR_CODES } from '../hooks/use-create-profile-validation'
import { AccountType } from '../hooks/use-create-standard-profile'
import { useResetSwrCache } from '../hooks/use-swr-reset-cache'
import type { Tokens } from '../lib/auth'
import * as Auth from '../lib/auth'
import { isMfaPending } from '../lib/auth'
import { isSuccess } from '../lib/request'
import { ResponseSuccess } from '../lib/request/make-request'
import { useSubscriptionState } from '../subscriptions/hooks/useSubscription'
import { BaseProfile } from '../util/auth'
import { mixPanelIdentify, mixPanelReset, mixPanelTrackEvent } from '../util/mixpanel'
import { canPatientAddDependents, getUserProfileFromId, getUserProfileText } from '../util/profile'
import { addSentryBreadcrumb } from '../util/sentry'

import { useSessionCart } from './cart'
import { useConfig } from './config'

export enum UserLoginErrorType {
  BLOCKED = 'BLOCKED_USER',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  PHONE_CONFLICT = 'PHONE_CONFLICT',
}

export interface AuthState {
  continueNavigationUrlPath?: string
  profile?: CompleteUser
  socialUser?: SocialData
  isReady: boolean
  isLoading: boolean
  isLoggedIn: boolean
  isNewAccount: boolean
}

export interface AuthContextType extends AuthState {
  registerDependantProfile: (profile: BaseProfile) => Promise<void>
  registerPatientAndLogin: (profile: Profile, token: string, accountType: AccountType, userId?: number) => Promise<void>
  validatePatientProfileData: (
    profile: Profile,
    token: string,
    accountType: AccountType,
    userId?: number
  ) => Promise<void>
  socialAuth: (user: SocialData, accessToken: string) => Promise<void>
  verifyPassword: (
    phone: string,
    password: string
  ) => Promise<
    | { kind: 'login-completed' }
    | { kind: 'mfa-check-required'; accessToken: string }
    | { kind: 'max-session-reached'; maxSessionsToken: string }
  >
  registerPhone: (countryCode: CountryCode, phone: string) => Promise<ResponseSuccess<{ accessToken: string }>>
  verifyCodeAndLogin: (
    code: string,
    token: string
  ) => Promise<{ kind: 'success' } | { kind: 'max-session-reached'; maxSessionsToken: string }>
  forceLogin: (maxSessionsToken: string) => Promise<void>
  saveTokens: (tokens: Tokens, isNewAccount: boolean) => void
  logout: () => Promise<void>
  confirmMfaCode: (code: string, accessToken: string) => Promise<ResponseSuccess<{ accessToken: string }>>
  hasDependantAccounts: () => boolean
  isDependantAccount: (patientId?: number) => boolean
  getFamilyProfileName: (patientId?: number) => string
  deleteProfile: (patientId: number) => Promise<void>
  reloadProfile: () => Promise<void>
  setContinueNavigationUrlPath: (path?: string) => void
}

export const AuthContext = React.createContext<AuthContextType | undefined>(undefined)

const initialState: AuthState = {
  isReady: false,
  isLoading: false,
  isLoggedIn: false,
  isNewAccount: false,
  continueNavigationUrlPath: undefined,
}

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { config, updateConfig } = useConfig()
  const [authState, setAuthState] = useState<AuthState>(initialState)
  const { removeProductFromCart } = useSessionCart()
  const { resetInactivityStatus } = useAppStatus()
  const resetSwrCache = useResetSwrCache()
  const { clearState: clearSubscriptionState } = useSubscriptionState()

  // On initial load, check if user is logged in and load the profile
  useEffect(() => {
    async function initialise() {
      try {
        setAuthState(s => ({ ...s, isLoading: true }))

        const token = await Auth.getToken()
        if (!token) {
          throw new Error('Unauthorized')
        }

        const profile = await Auth.getUser()
        setAuthState({
          isLoading: false,
          isLoggedIn: true,
          isReady: true,
          profile: profile.data ?? undefined,
          isNewAccount: false,
        })
      } catch (err) {
        setAuthState(s => ({ ...s, isLoading: false, isReady: true }))
      }
    }
    initialise()
  }, [])

  // We want to reset the inactivity status when the user auth state changes
  useEffect(() => {
    resetInactivityStatus()
  }, [authState.isLoggedIn]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateConfig({ canPatientAddDependents: canPatientAddDependents({ config, profile: authState.profile }) })
  }, [authState.profile?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const setContinueNavigationUrlPath: AuthContextType['setContinueNavigationUrlPath'] = useCallback(
    (continueNavigationUrlPath?: string) => {
      setAuthState(s => ({ ...s, continueNavigationUrlPath }))
    },
    []
  )

  const saveTokens: AuthContextType['saveTokens'] = useCallback(async (tokens: Tokens, isNewAccount: boolean) => {
    clearSubscriptionState()
    setAuthState(s => ({ ...s, isLoading: true }))

    Auth.saveToken('token', tokens.accessToken)
    Auth.saveToken('refreshToken', tokens.refreshToken)

    const profile = await Auth.getUser()

    await mixPanelIdentify(profile.data?.email as string)
    mixPanelTrackEvent('User Logged In', {
      previous_step: window.location.href,
    })

    setAuthState(s => ({
      ...s,
      isLoading: false,
      isReady: true,
      isLoggedIn: true,
      profile: profile.data ?? undefined,
      isNewAccount,
    }))
  }, [])

  const socialAuth: AuthContextType['socialAuth'] = useCallback(async (user, accessToken) => {
    Auth.saveToken('token', accessToken)
    setAuthState(s => ({
      ...s,
      socialUser: user,
    }))
  }, [])

  const validatePatientProfileData: AuthContextType['validatePatientProfileData'] = useCallback(
    async (profile: Profile, token, accountType) => {
      const resp = await Auth.register.patientValidate(profile, token, accountType)

      if (!isSuccess(resp)) {
        if (['EMAIL_CONFLICT', 'PHONE_CONFLICT', 'CPF_GET_DATA_ERROR', 'CPF_DUPLICATED'].includes(resp.error!.code)) {
          throw new Error(resp.error!.code)
        }

        throw new Error('Failed to validate patient')
      }
    },
    []
  )

  const registerDependantProfile: AuthContextType['registerDependantProfile'] = useCallback(
    async (profile: BaseProfile) => {
      const resp = await Auth.register.dependant(profile)

      if (!isSuccess(resp)) {
        const errorCode = resp.error?.code as PROFILE_VALIDATION_ERROR_CODES
        if (Object.values(PROFILE_VALIDATION_ERROR_CODES).includes(errorCode)) {
          throw new Error(resp.error!.code)
        }

        throw new Error('Failed to register')
      }

      addSentryBreadcrumb('Register Dependant')
    },
    []
  )

  const registerPatientAndLogin: AuthContextType['registerPatientAndLogin'] = useCallback(
    async (profile: Profile, token, accountType) => {
      const resp = await Auth.register.patient(profile, token, accountType)

      if (!isSuccess(resp) || !resp.data?.accessToken) {
        const errorCode = resp.error?.code as PROFILE_VALIDATION_ERROR_CODES
        if (Object.values(PROFILE_VALIDATION_ERROR_CODES).includes(errorCode)) {
          throw new Error(resp.error!.code)
        }

        throw new Error('Failed to register')
      }

      addSentryBreadcrumb('Register')
      saveTokens(resp.data, true)
    },
    [saveTokens]
  )

  const verifyPassword: AuthContextType['verifyPassword'] = useCallback(
    async (phone, password) => {
      const resp = await Auth.login.callback(phone, password)
      if (resp.status === 429 && resp.error && 'maxSessionsToken' in resp.error && resp.error.maxSessionsToken) {
        return { kind: 'max-session-reached', maxSessionsToken: resp.error.maxSessionsToken }
      }
      if (!isSuccess(resp) || !('accessToken' in resp.data)) {
        if (resp.error?.code === UserLoginErrorType.BLOCKED) {
          throw new Error(resp.error.code)
        }
        throw new Error('Failed to login')
      }

      const accessToken = resp.data.accessToken
      const mfaRquired = config.enableMfa || isMfaPending(accessToken)

      if (mfaRquired) {
        return { kind: 'mfa-check-required', accessToken }
      }

      await saveTokens(resp.data, false)
      addSentryBreadcrumb('Login (MFA disabled)')
      return { kind: 'login-completed' }
    },
    [config.enableMfa, saveTokens]
  )

  const registerPhone: AuthContextType['registerPhone'] = useCallback(async (countryCode, phone) => {
    const phoneNumber = parsePhoneNumber(phone, countryCode).number
    const resp = await Auth.register.phone(phoneNumber)

    if (!isSuccess(resp) || !resp.data?.accessToken) {
      if (resp.error?.code === UserLoginErrorType.PHONE_CONFLICT) {
        throw new Error(resp.error.code)
      }
      throw new Error('Phone verification failed')
    }

    return resp
  }, [])

  const confirmMfaCode: AuthContextType['confirmMfaCode'] = useCallback(async (code, accessToken) => {
    const resp = await Auth.register.confirmation(code, accessToken)

    if (!isSuccess(resp) || !resp.data?.accessToken) {
      throw new Error('MFA confirmation failed')
    }

    return resp
  }, [])

  const verifyCodeAndLogin: AuthContextType['verifyCodeAndLogin'] = useCallback(
    async (code, token) => {
      const resp = await Auth.login.mfa(code, token)
      if (resp.status === 429 && resp.error && 'maxSessionsToken' in resp.error && resp.error.maxSessionsToken) {
        return { kind: 'max-session-reached', maxSessionsToken: resp.error.maxSessionsToken }
      }
      if (!isSuccess(resp) || !('accessToken' in resp.data)) {
        throw new Error('Failed to login')
      }
      saveTokens(resp.data, false)
      addSentryBreadcrumb('Login (MFA enabled)')
      return { kind: 'success' }
    },
    [saveTokens]
  )

  const forceLogin: AuthContextType['forceLogin'] = useCallback(
    async (maxSessionsToken: string) => {
      const resp = await Auth.login.forceLogin(maxSessionsToken)

      if (resp.error) {
        throw new Error(resp.error.message)
      }
      saveTokens(resp.data, false)
      addSentryBreadcrumb('Force login')
    },
    [saveTokens]
  )

  const logout: AuthContextType['logout'] = useCallback(async () => {
    setAuthState(s => ({ ...s, isLoading: true }))
    clearSubscriptionState()
    await Auth.logout()
    removeProductFromCart()
    resetSwrCache()
    setAuthState({ ...initialState, isLoading: false, isReady: true })
    mixPanelReset()
    addSentryBreadcrumb('Logout')
  }, [removeProductFromCart, resetSwrCache])

  const reloadProfile: AuthContextType['reloadProfile'] = useCallback(async () => {
    setAuthState(s => ({ ...s, isLoading: true }))
    const user = await Auth.getUser()
    setAuthState(s => ({ ...s, isLoading: false, profile: user.data ?? undefined }))
  }, [])

  const deleteProfile: AuthContextType['deleteProfile'] = useCallback(
    async patientId => {
      if (!authState.profile) {
        throw new Error('No profile to delete')
      }

      const resp = await Auth.deleteUserProfile(patientId)
      if (!isSuccess(resp) || !resp.data) {
        throw new Error('Failed to delete profile')
      }
    },
    [authState.profile]
  )

  const hasDependantAccounts: AuthContextType['hasDependantAccounts'] = useCallback(() => {
    if (!config.canPatientAddDependents) return false

    return !!authState.profile?.dependants?.length
  }, [authState.profile?.dependants?.length, config.canPatientAddDependents])

  const isDependantAccount: AuthContextType['isDependantAccount'] = useCallback(
    (patientId?: number) => {
      if (!config.canPatientAddDependents) return false

      return !!getUserProfileFromId(authState.profile, patientId)?.isDependant
    },
    [authState.profile, config.canPatientAddDependents]
  )

  const getFamilyProfileName: AuthContextType['getFamilyProfileName'] = useCallback(
    (patientId?: number) => {
      if (!config.canPatientAddDependents) return ''

      return getUserProfileText({ user: getUserProfileFromId(authState.profile, patientId) })
    },
    [authState.profile, config.canPatientAddDependents]
  )

  const context = useMemo<AuthContextType>(
    () => ({
      socialAuth,
      confirmMfaCode,
      deleteProfile,
      getFamilyProfileName,
      hasDependantAccounts,
      isDependantAccount,
      registerDependantProfile,
      registerPatientAndLogin,
      validatePatientProfileData,
      verifyPassword,
      registerPhone: registerPhone,
      verifyCodeAndLogin,
      forceLogin,
      saveTokens,
      logout,
      reloadProfile,
      setContinueNavigationUrlPath,
      ...authState,
    }),
    [
      socialAuth,
      confirmMfaCode,
      deleteProfile,
      getFamilyProfileName,
      hasDependantAccounts,
      isDependantAccount,
      registerDependantProfile,
      registerPatientAndLogin,
      validatePatientProfileData,
      verifyPassword,
      registerPhone,
      verifyCodeAndLogin,
      forceLogin,
      saveTokens,
      logout,
      reloadProfile,
      setContinueNavigationUrlPath,
      authState,
    ]
  )

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth() must be wrapped inside AuthProvider.')
  }
  return context
}
